import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import DropDownSearch from "@components/ui/dropdown/DropDownSearch";
import { sample_repertorys } from "@screens/sampleData";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import Button from "@components/ui/buttons/Button";
import Dropzone from "@components/ui/blocks/Dropzone";
import { useState } from "react";

export interface FolderOption {
  value: string;
  label: string;
}

export interface ControlPointOption {
  value: string;
  label: string;
}

interface DropFileModalProps {
  onClose: () => void;
  onSave?: (data: {
    files: File[];
    repertory: string;
    electronicSignature: boolean;
    internalDocument: boolean;
    controlpointId?: string;
  }) => void;
  title?: string;
  iconName?: string;
  saveButtonLabel?: string;
  showRepertorySelector?: boolean;
  showElectronicSignature?: boolean;
  showInternalDocument?: boolean;
  acceptedFileTypes?: string;
  folderOptions?: FolderOption[];
  showImagePreview?: boolean;
  controlpointOptions?: ControlPointOption[];
}

export const DropFileModal = ({
  onClose,
  onSave,
  title = "Déposer un document",
  iconName = "file",
  saveButtonLabel = "Enregistrer",
  showRepertorySelector = true,
  showElectronicSignature = true,
  showInternalDocument = true,
  acceptedFileTypes,
  folderOptions,
  showImagePreview = false,
  controlpointOptions,
}: DropFileModalProps) => {
  const options =
    folderOptions || sample_repertorys.map((r) => ({ value: r, label: r }));

  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [selectedRepertory, setSelectedRepertory] = useState<string>(
    options[0]?.value || "",
  );
  const [electronicSignature, setElectronicSignature] =
    useState<boolean>(false);
  const [internalDocument, setInternalDocument] = useState<boolean>(false);
  const [selectedControlpoint, setSelectedControlpoint] = useState<string>("");
  const handleSave = () => {
    if (droppedFiles.length === 0) {
      alert("Veuillez sélectionner au moins un fichier");
      return;
    }

    if (onSave) {
      onSave({
        files: droppedFiles,
        repertory: selectedRepertory,
        electronicSignature,
        internalDocument,
        controlpointId: selectedControlpoint || undefined,
      });
    }
  };

  return (
    <BaseModal
      title={title}
      startIcon={<Icon1 name={iconName} size={20}></Icon1>}
      onClose={onClose}
    >
      <Dropzone
        onDrop={(files) => {
          setDroppedFiles(files);
        }}
        files={droppedFiles}
        setSelectedDroppedFiles={setDroppedFiles}
        onDelete={(file) =>
          setDroppedFiles(droppedFiles.filter((f) => f !== file))
        }
        showImagePreview={showImagePreview}
        acceptTypes={
          acceptedFileTypes === "image/*"
            ? {
                "image/jpeg": [".jpg", ".jpeg"],
                "image/png": [".png"],
                "image/webp": [".webp"],
              }
            : undefined
        }
      ></Dropzone>
      <div className="bg-white flex flex-col gap-2 p-4 border-t-1">
        {showRepertorySelector && (
          <DropDown
            options={options}
            label="Choisir un répertoire"
            value={selectedRepertory}
            onSelect={(value) => {
              setSelectedRepertory(value as string);
            }}
          ></DropDown>
        )}

        {/* {showElectronicSignature && (
          <SwitchBrick
            title="Signature électronique"
            description="Activer la signature électronique pour ce document"
            checked={electronicSignature}
            onChange={(newState) => {
              setElectronicSignature(newState);
            }}
          ></SwitchBrick>
        )} */}

        {showInternalDocument && (
          <SwitchBrick
            title="Document interne"
            description="Ne pas partager ce document avec le client"
            checked={internalDocument}
            onChange={(newState) => {
              setInternalDocument(newState);
            }}
          ></SwitchBrick>
        )}

        {controlpointOptions && controlpointOptions.length > 0 && (
          <DropDownSearch
            options={[{ value: "", label: "Aucun" }, ...controlpointOptions]}
            label="Associer à un point de contrôle"
            placeholder="Rechercher un point de contrôle..."
            value={selectedControlpoint}
            onSelect={(value) => {
              setSelectedControlpoint(value as string);
            }}
          />
        )}
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={saveButtonLabel}
          onClick={handleSave}
        ></Button>
      </div>
    </BaseModal>
  );
};
